/* eslint-disable max-lines */
import {
  StyleDc,
  PolygonSymbolDc,
  PolylineSymbolDc,
  RasterSymbolDc,
  ImagePointSymbolDc,
  MaskedImagePointSymbolDc,
  CompositeSymbolDc,
  PointLabelSymbolDc,
  PolygonLabelSymbolDc,
  PolylineLabelSymbolDc,
  CirclePointSymbolDc,
  SquarePointSymbolDc,
  MultipointSymbolDc,
  ParameterDcDouble,
  ParameterDcColor,
  TextBackgroundDc,
  SolidStrokeBrushDc,
  DashedBrushDc,
  HatchBrushDc,
  PatternBrushDc,
  SimplePolylineSymbolDc,
  FontWeight,
  PolygonCenterLabelSymbolDc,
  SvgPointSymbolDc,
  CombinedPolylineSymbolDc,
  H3GridSymbolDc,
  ClusterSymbolDc,
  LineEndingType,
  ParameterDcDoubleArray,
} from "@evergis/api";
import { isParameterByAttribute } from "../evergis/core/classification/getFromClassified";
import { Style } from "../ducks/style/style";
import { ClassificationParameterExtended } from "./classification";

// TODO move all type helpers
type ToClientSymbolField<F, IsClassifyable extends boolean> = F extends ParameterDcDouble
  ? IsClassifyable extends true
    ? ClassifyAble<number>
    : number
  : F extends ParameterDcColor
  ? IsClassifyable extends true
    ? ClassifyAble<string>
    : string
  : F extends ParameterDcDoubleArray
  ? IsClassifyable extends true
    ? ClassifyAble<number[]>
    : number[]
  : F;

type ExtraClientSymbolFields = {
  offset: Offset;
  fontWeight: FontWeight;
  childSymbols: StyleSymbolType[];
  stroke: StrokeBrushType;
  fill: FillBrushType;
  textBackground: TextBackgroundType;
  beginning: null | LineEndingType;
  beginningSize: number;
  ending: null | LineEndingType;
  endingSize: number;
  angle: CalculatedAble<number>;
};

type ClientSymbol<SymbolType, SymbolDcType, NonClassifiedFields extends keyof SymbolDcType = never> = {
  [K in keyof SymbolDcType]: K extends "type"
    ? SymbolType
    : K extends keyof ExtraClientSymbolFields
    ? ExtraClientSymbolFields[K]
    : ToClientSymbolField<SymbolDcType[K], K extends NonClassifiedFields ? false : true>;
};

export type ParameterByAttributeValue<Value> = {
  condition: string;
  value: Value;
  title: string | null;
};

export type ParameterByAttribute<Value> = {
  type: "byAttribute" | "byScale";
  defaultValue: Value;
  title: string | null;
  values: ParameterByAttributeValue<Value>[];
};

export type CalculatedParameter<Value> = {
  type: "calculated";
  expression: string;
  defaultValue: Value;
  title?: string | null;
};

export type ClassifyAble<Value> = Value | ParameterByAttribute<Value>;
export type CalculatedAble<Value> = Value | CalculatedParameter<Value>;

export type ParameterValue = string | number | number[] | Date;
export type ParameterValueWithSymbol = ParameterValue | StyleSymbolType | Date;
export type ClassifiedParameterValue = ClassifyAble<ParameterValueWithSymbol>;
export type UnidentifiedParameterValue =
  | ParameterValueWithSymbol
  | Partial<ClassifyAble<string | number | StyleSymbolType>>
  | Partial<CalculatedAble<string | number>>;
export type PointSymbol = ClientSymbol<"circlePointSymbol", CirclePointSymbolDc> & {
  angle?: CalculatedAble<number>;
};
export type SvgPointSymbol = ClientSymbol<"svgPointSymbol", SvgPointSymbolDc>;
export type CombinedPolylineSymbol = ClientSymbol<"combinedPolylineSymbol", CombinedPolylineSymbolDc>;
export type H3Symbol = ClientSymbol<"h3grid", H3GridSymbolDc>;
export type ClusterSymbol = ClientSymbol<"cluster", ClusterSymbolDc>;
export type SquareSymbol = ClientSymbol<"squarePointSymbol", SquarePointSymbolDc> & {
  angle?: CalculatedAble<number>;
};
export type MaskedImageSymbol = ClientSymbol<"maskedImagePointSymbol", MaskedImagePointSymbolDc>;
export type MultipointSymbol = Omit<MultipointSymbolDc, "pointSymbol"> & {
  pointSymbol: PointSymbol;
};
export type StaticImageSymbol = ClientSymbol<"imagePointSymbol", ImagePointSymbolDc>;
export type PolygonSymbol<Fill = FillBrushType, Stroke = StrokeBrushType> = ClientSymbol<
  "polygonSymbol",
  PolygonSymbolDc
> & {
  fill: Fill;
  stroke: Stroke;
};
export type SimplePolylineSymbol = ClientSymbol<"simplePolylineSymbol", SimplePolylineSymbolDc>;
export type PolylineSymbol = ClientSymbol<"polylineSymbol", PolylineSymbolDc>;
export type RasterSymbol = ClientSymbol<"compositeSymbol", RasterSymbolDc> & {
  childSymbols: Array<PolygonSymbolDc | RasterSymbolDc>;
};
export type CompositeSymbolType = ClientSymbol<"compositeSymbol", CompositeSymbolDc>;

export type ParameterByAttributeValues = ParameterByAttributeValue<ParameterValue>[];

type NonClassifyableLabelFields = "fontColor" | "fontSize" | "haloColor";

export type PointLabelSymbol = ClientSymbol<"labelPointSymbol", PointLabelSymbolDc, NonClassifyableLabelFields>;
export type PolygonLabelSymbol = ClientSymbol<"polygonLabelSymbol", PolygonLabelSymbolDc, NonClassifyableLabelFields>;
export type PolygonCenterLabelSymbol = ClientSymbol<
  "polygonCenterLabelSymbol",
  PolygonCenterLabelSymbolDc,
  NonClassifyableLabelFields
>;
export type PolylineLabelSymbol = ClientSymbol<
  "polylineLabelSymbol",
  PolylineLabelSymbolDc,
  NonClassifyableLabelFields
>;

export type PolylineMiterSymbol = ClientSymbol<"polylineMiterSymbol", SimplePolylineSymbolDc>;
export type PolylineSymbols = PolylineSymbol | SimplePolylineSymbol;

export type SimpleOffset = [number, number];
export type ClassifiedOffset = [ParameterByAttribute<number>, ParameterByAttribute<number>];
export type Offset = SimpleOffset | ClassifiedOffset;
export type LabelType = PolylineLabelSymbol | PolygonLabelSymbol | PolygonCenterLabelSymbol | PointLabelSymbol;
export type PointSymbolType = SvgPointSymbol | PointSymbol | SquareSymbol | MaskedImageSymbol | StaticImageSymbol;
export type SimpleShapeSymbol = PointSymbol | SquareSymbol;
export type ImageSymbol = MaskedImageSymbol | StaticImageSymbol;
export type StyleSymbolTypes = StyleSymbolType["type"];
export type NonLabelStyleSymbolTypes = Exclude<StyleSymbolType, LabelType>["type"];
export type LabelStyleSymbolTypes = LabelType["type"];
export type UnknownSymbol = StyleDc["symbol"] | StyleSymbolType;
export type LayerSymbol = PointSymbol | PolylineSymbols | PolygonSymbol | MultipointSymbol | RasterSymbol;
export type FillStyledSymbols = PolygonSymbol;
export type StrokeStyledSymbols = PolygonSymbol | PolylineSymbols;
export type AngleSymbolTypes = PointSymbolType;
export type SolidBrush = ClientSymbol<"solid", SolidStrokeBrushDc>;
export type DashedBrush = ClientSymbol<"dashed", DashedBrushDc>;
export type HatchBrush = ClientSymbol<"hatch", HatchBrushDc>;
export type PatternBrush = ClientSymbol<"pattern", PatternBrushDc>;
export type StrokeBrushType = SolidBrush | DashedBrush;
export type FillBrushType = SolidBrush | HatchBrush | PatternBrush;

export const isStringParameterValue = (parameterValue: ParameterValueWithSymbol): parameterValue is string =>
  typeof parameterValue === "string";

export const isParameterValueSimple = (parameterValue: ParameterValueWithSymbol): parameterValue is ParameterValue =>
  typeof parameterValue === "number" || typeof parameterValue === "string";

export const isParameterValueSymbol = (parameterValue: ParameterValueWithSymbol): parameterValue is StyleSymbolType =>
  !isParameterValueSimple(parameterValue);

function symbolTypeGuard<ClientSymbolPart extends Input, Input extends { type?: string } = { type?: string }>(
  typeOrValidator: ClientSymbolPart["type"] | ((symbol: Input) => boolean),
): (symbol: Input) => symbol is ClientSymbolPart {
  if (typeof typeOrValidator === "string")
    return (symbol: Input): symbol is ClientSymbolPart => symbol && symbol.type === typeOrValidator;

  return (symbol: Input): symbol is ClientSymbolPart => typeOrValidator(symbol);
}

export const isPointSymbol = symbolTypeGuard<PointSymbol>("circlePointSymbol");
export const isSquareSymbol = symbolTypeGuard<SquareSymbol>("squarePointSymbol");
export const isMaskedImageSymbol = symbolTypeGuard<MaskedImageSymbol>("maskedImagePointSymbol");
export const isStaticImageSymbol = symbolTypeGuard<StaticImageSymbol>("imagePointSymbol");
export const isPolygonSymbol = symbolTypeGuard<PolygonSymbol>("polygonSymbol");
export const isPolylineSymbol = symbolTypeGuard<PolylineSymbol>("polylineSymbol");
export const isSimplePolylineSymbol = symbolTypeGuard<SimplePolylineSymbol>("simplePolylineSymbol");
export const isCombinedPolylineSymbol = symbolTypeGuard<CombinedPolylineSymbol>("combinedPolylineSymbol");
export const isPolylineSymbols = symbolTypeGuard<PolylineSymbols>(
  symbol =>
    isPolylineSymbol(symbol as PolylineSymbol) ||
    isSimplePolylineSymbol(symbol as PolylineSymbol) ||
    isCombinedPolylineSymbol(symbol as PolylineSymbol),
);
export const isPointLabelSymbol = symbolTypeGuard<PointLabelSymbol>("labelPointSymbol");
export const isPolygonLabelSymbol = symbolTypeGuard<PolygonLabelSymbol>("polygonLabelSymbol");
export const isPolygonCenterLabelSymbol = symbolTypeGuard<PolygonCenterLabelSymbol>("polygonCenterLabelSymbol");
export const isPolylineLabelSymbol = symbolTypeGuard<PolylineLabelSymbol>("polylineLabelSymbol");
export const isCompositeSymbol = symbolTypeGuard<CompositeSymbolType>("compositeSymbol");
export const isClusterSymbol = symbolTypeGuard<ClusterSymbol>("cluster");
export const isRasterSymbol = symbolTypeGuard<RasterSymbol>("compositeSymbol");
export const isH3GridSymbol = symbolTypeGuard<H3Symbol>("h3grid");

export const isClusterFillColor = (symbol: StyleSymbolType, parameter: ClassificationParameterExtended) => {
  return isClusterSymbol(symbol) && parameter === "fillColor";
};

export const isClusterSingleObject = (symbol: StyleSymbolType, parameter: ClassificationParameterExtended) => {
  return isClusterSymbol(symbol) && parameter !== "fillColor";
};

export type StyleSymbolType =
  | SvgPointSymbol
  | H3Symbol
  | ClusterSymbol
  | PointSymbol
  | SquareSymbol
  | MaskedImageSymbol
  | StaticImageSymbol
  | PolygonSymbol
  | PolylineSymbol
  | SimplePolylineSymbol
  | PointLabelSymbol
  | PolygonLabelSymbol
  | PolylineLabelSymbol
  | CompositeSymbolType
  | RasterSymbol
  | PolylineMiterSymbol
  | MultipointSymbol
  | CombinedPolylineSymbol;

export const isHatchBrush = symbolTypeGuard<HatchBrush>("hatch");
export const isPatternBrush = symbolTypeGuard<PatternBrush>("pattern");
export const isDashedBrush = symbolTypeGuard<DashedBrush>("dashed");
export const isSolidBrush = symbolTypeGuard<SolidBrush>("solid");

export const isPolygonHasPatternBrush = (symbol: UnknownSymbol): symbol is PolygonSymbol<PatternBrush> =>
  isPolygonSymbol(symbol as PolygonSymbol) && isPatternBrush((symbol as PolygonSymbol).fill);
export const isPolygonHasHatchBrush = (symbol: UnknownSymbol): symbol is PolygonSymbol<HatchBrush> =>
  isPolygonSymbol(symbol as PolygonSymbol) && isHatchBrush((symbol as PolygonSymbol).fill);

export type TextBackgroundType = Omit<TextBackgroundDc, "fill" | "stroke"> & {
  fill: Unclassify<FillBrushType>;
  stroke: Unclassify<StrokeBrushType>;
};

export type NoLineMiterType = {
  type: LineEndingType.None;
};

type LineMiterBase<T extends LineEndingType> = {
  type: T;
  color?: ClassifyAble<string>;
};

export type ArrowLineType = LineMiterBase<LineEndingType.Arrow> & {
  strokeWidth?: number;
  arrowLength?: number;
  arrowWidth?: number;
};
export type FilledArrowLineMiterType = LineMiterBase<LineEndingType.FilledArrow> & {
  arrowLength?: number;
  arrowWidth?: number;
};
export type SquareLineMiterType = LineMiterBase<LineEndingType.Square> & {
  size?: number;
  strokeWidth?: number;
};
export type FilledSquareLineMiterType = LineMiterBase<LineEndingType.FilledSquare> & {
  size?: number;
};
export type CircleLineMiterType = LineMiterBase<LineEndingType.Circle> & {
  size?: number;
  strokeWidth?: number;
};
export type FilledCircleLineMiterType = LineMiterBase<LineEndingType.FilledCircle> & {
  size?: number;
};

export type FilledLineMitters = FilledArrowLineMiterType | FilledSquareLineMiterType | FilledCircleLineMiterType;
export type ArrowLineMiters = ArrowLineType | FilledArrowLineMiterType;
export type SquareLineMiters = SquareLineMiterType | FilledSquareLineMiterType;
export type CircleLineMiters = CircleLineMiterType | FilledCircleLineMiterType;
export type LineMiterType = NoLineMiterType | ArrowLineMiters | SquareLineMiters | CircleLineMiters;

export const isImageSymbol = (symbol: UnknownSymbol): symbol is ImageSymbol =>
  isMaskedImageSymbol(symbol as ImageSymbol) || isStaticImageSymbol(symbol as ImageSymbol);

export const isSimpleOffset = (offset: Offset): offset is SimpleOffset =>
  offset && !isParameterByAttribute(offset[0]) && !isParameterByAttribute(offset[1]);

export const isTwoDimensionalSymbol = (symbol: UnknownSymbol): symbol is ImageSymbol => "width" in symbol;

export const isSimpleShapeSymbol = symbolTypeGuard<SimpleShapeSymbol>(
  symbol => isPointSymbol(symbol) || isSquareSymbol(symbol),
);

export const isSizableSymbol = symbolTypeGuard<SimpleShapeSymbol>(symbol => "size" in symbol);

export const isSvgPointSymbol = symbolTypeGuard<SvgPointSymbol>(symbol => "data" in symbol);

export const isSymbolWithOffset = (symbol: UnknownSymbol): symbol is PointSymbolType =>
  symbol && "offset" in symbol && !!symbol.offset;

export const isStyle = (value: Record<string, unknown>): value is Style => "symbol" in value;

export const isStrokeStyledSymbol = (symbol: UnknownSymbol): symbol is StrokeStyledSymbols =>
  isPolygonSymbol(symbol as StrokeStyledSymbols) || isPolylineSymbols(symbol as StrokeStyledSymbols);

export const isLabelSymbol = (symbol: UnknownSymbol): symbol is LabelType =>
  isPointLabelSymbol(symbol as LabelType) ||
  isPolygonLabelSymbol(symbol as LabelType) ||
  isPolygonCenterLabelSymbol(symbol as LabelType) ||
  isPolylineLabelSymbol(symbol as LabelType);

export const isMiterExist = (miter?: undefined | LineMiterType): miter is Exclude<LineMiterType, NoLineMiterType> =>
  miter && miter.type !== LineEndingType.None;

export const isScalablePolylineSymbol = (symbol: PolylineSymbol | SimplePolylineSymbol) =>
  (symbol.beginning !== null && symbol.ending !== null) || (symbol.stroke && symbol.stroke.type !== "solid");

export const isFilledLineMitter = (miter: LineMiterType): miter is FilledLineMitters =>
  miter.type === LineEndingType.FilledCircle ||
  miter.type === LineEndingType.FilledArrow ||
  miter.type === LineEndingType.FilledSquare;

export const isArrowLineMiter = (miter: LineMiterType): miter is ArrowLineMiters =>
  miter.type === LineEndingType.Arrow || miter.type === LineEndingType.FilledArrow;

export const isSquareLineMiter = (miter: LineMiterType): miter is SquareLineMiters =>
  miter.type === LineEndingType.Square || miter.type === LineEndingType.FilledSquare;

export const isCircleLineMiter = (miter: LineMiterType): miter is CircleLineMiters =>
  miter.type === LineEndingType.Circle || miter.type === LineEndingType.FilledCircle;

export const isSimpleSymbol = (
  symbol: UnknownSymbol,
): symbol is Exclude<StyleSymbolType, LabelType | CompositeSymbolType> =>
  !isLabelSymbol(symbol) &&
  !isCompositeSymbol(symbol as CompositeSymbolType) &&
  !isRasterSymbol(symbol as CompositeSymbolType);

export type Unclassify<T> = {
  [K in keyof T]: T[K] extends ClassifyAble<infer V> ? V : T[K] extends CalculatedAble<infer V2> ? V2 : T[K];
};

export type SymbolValues<T extends { type?: string }> = Omit<T, "type">;
